/**
 * PricingTab — edit the customer-facing prices used by the mobile app.
 *
 * Loads/saves the singleton pricing config on the Node backend (/api/pricing).
 * Values are stored in cents; shown/edited here in dollars. Changes take effect
 * in the app the next time it launches (it fetches pricing on start), so no app
 * update is needed to change a price.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    InputAdornment,
} from '@mui/material';
import { styled } from '@mui/system';
import { getPricing, updatePricing } from '../../services/adminService';
import { VALET, FONTS, TYPE } from '../../theme';

const FIELDS = [
    {
        key: 'parkingCents',
        label: 'Standard Parking',
        help: 'Flat fee to park a car.',
        perHour: false,
    },
    {
        key: 'parkAndRetrieveCents',
        label: 'Park & Retrieve',
        help: 'Flat fee — a valet returns the car at the end of the reservation.',
        perHour: false,
    },
    {
        key: 'aspCents',
        label: 'ASP Parking',
        help: 'Alternate-side parking, flat fee.',
        perHour: false,
    },
    {
        key: 'retrievalCents',
        label: 'Retrieval',
        help: 'On-demand — a valet brings the car to the customer.',
        perHour: false,
    },
    {
        key: 'carWatchPerHourCents',
        label: 'Car Watch',
        help: 'Per hour — a valet keeps eyes on the parked car.',
        perHour: true,
    },
];

const centsToDollars = (c) => (Number(c || 0) / 100).toFixed(2);
const dollarsToCents = (d) => Math.round(parseFloat(d) * 100);

const SectionEyebrow = styled(Typography)({
    ...TYPE.eyebrow,
    marginBottom: '6px',
});

const SectionTitle = styled(Typography)({
    ...TYPE.sectionTitle,
    marginBottom: '4px',
});

const Card = styled(Box)({
    backgroundColor: VALET.surface,
    border: `1px solid ${VALET.border}`,
    borderRadius: '14px',
    boxShadow: VALET.shadow,
    padding: '8px 24px',
    maxWidth: 640,
    '@media (max-width: 768px)': {
        padding: '4px 14px',
    },
});

const Row = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
    padding: '20px 0',
    borderBottom: `1px solid ${VALET.border}`,
    '&:last-of-type': { borderBottom: 'none' },
    // A price label and its input cannot share a line on a phone without the
    // input shrinking to a few characters. Stack them and let the field run
    // the full width, which is also a much bigger tap target.
    '@media (max-width: 768px)': {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '10px',
        padding: '16px 0',
    },
});

const FieldLabel = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '15px',
    fontWeight: 600,
    color: VALET.textPrimary,
});

const FieldHelp = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '12.5px',
    color: VALET.textMuted,
    marginTop: '2px',
    maxWidth: 360,
});

const SaveButton = styled(Button)({
    backgroundColor: VALET.green,
    color: '#ffffff',
    textTransform: 'none',
    fontFamily: FONTS.sans,
    fontWeight: 600,
    fontSize: '14px',
    padding: '9px 22px',
    borderRadius: '10px',
    boxShadow: 'none',
    '&:hover': { backgroundColor: VALET.greenDark, boxShadow: 'none' },
    '&.Mui-disabled': { backgroundColor: VALET.border, color: VALET.textMuted },
});

const ResetButton = styled(Button)({
    color: VALET.textSecondary,
    textTransform: 'none',
    fontFamily: FONTS.sans,
    fontWeight: 500,
    fontSize: '14px',
    padding: '9px 18px',
    borderRadius: '10px',
});

export default function PricingTab() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [values, setValues] = useState({});
    const [original, setOriginal] = useState({});
    const [updatedAt, setUpdatedAt] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null); // { severity, msg }

    const applyPricing = (p) => {
        const v = {};
        FIELDS.forEach((f) => {
            v[f.key] = centsToDollars(p[f.key]);
        });
        setValues(v);
        setOriginal(v);
        setUpdatedAt(p.updatedAt || null);
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getPricing();
            applyPricing(res.pricing || res);
        } catch (err) {
            setError(err.message || 'Failed to load pricing');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const dirty = FIELDS.some((f) => values[f.key] !== original[f.key]);

    const handleChange = (key, raw) => {
        // Allow only digits + one decimal point, up to 2 decimals.
        const cleaned = raw.replace(/[^0-9.]/g, '');
        const parts = cleaned.split('.');
        const normalized =
            parts.length > 1
                ? `${parts[0]}.${parts[1].slice(0, 2)}`
                : cleaned;
        setValues((prev) => ({ ...prev, [key]: normalized }));
    };

    const validate = () => {
        for (const f of FIELDS) {
            const n = parseFloat(values[f.key]);
            if (!Number.isFinite(n) || n < 0) {
                return `Enter a valid amount for ${f.label}.`;
            }
        }
        return null;
    };

    const handleSave = async () => {
        const err = validate();
        if (err) {
            setToast({ severity: 'error', msg: err });
            return;
        }
        const changed = FIELDS.filter((f) => values[f.key] !== original[f.key]);
        const summary = changed
            .map((f) => `• ${f.label}: $${values[f.key]}${f.perHour ? '/hr' : ''}`)
            .join('\n');
        if (
            !window.confirm(
                `Update these prices?\n\n${summary}\n\nThis changes what customers are charged in the app (takes effect on next app launch).`
            )
        ) {
            return;
        }
        setSaving(true);
        try {
            const payload = { updatedBy: 'dashboard' };
            FIELDS.forEach((f) => {
                payload[f.key] = dollarsToCents(values[f.key]);
            });
            const res = await updatePricing(payload);
            applyPricing(res.pricing || res);
            setToast({
                severity: 'success',
                msg: 'Prices saved. The app will use them the next time it launches.',
            });
        } catch (e) {
            setToast({ severity: 'error', msg: e.message || 'Failed to save' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 300,
                }}>
                <CircularProgress sx={{ color: VALET.green }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert
                severity="error"
                action={
                    <Button color="inherit" size="small" onClick={load}>
                        Retry
                    </Button>
                }>
                {error}
            </Alert>
        );
    }

    return (
        <Box>
            <SectionEyebrow>Pricing</SectionEyebrow>
            <SectionTitle>App prices</SectionTitle>
            <Typography
                sx={{
                    ...TYPE.bodySmall,
                    color: VALET.textSecondary,
                    marginBottom: '20px',
                    maxWidth: 640,
                }}>
                Set what customers pay in the app. Changes take effect the next
                time a customer opens the app — no app update needed.
            </Typography>

            <Card>
                {FIELDS.map((f) => (
                    <Row key={f.key}>
                        <Box>
                            <FieldLabel>{f.label}</FieldLabel>
                            <FieldHelp>{f.help}</FieldHelp>
                        </Box>
                        <TextField
                            value={values[f.key] ?? ''}
                            onChange={(e) => handleChange(f.key, e.target.value)}
                            size="small"
                            inputMode="decimal"
                            sx={{ width: 150 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">$</InputAdornment>
                                ),
                                endAdornment: f.perHour ? (
                                    <InputAdornment position="end">/ hr</InputAdornment>
                                ) : null,
                                sx: { fontFamily: FONTS.mono, fontSize: '15px' },
                            }}
                        />
                    </Row>
                ))}
            </Card>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: '20px',
                    maxWidth: 640,
                }}>
                <SaveButton onClick={handleSave} disabled={!dirty || saving}>
                    {saving ? 'Saving…' : 'Save prices'}
                </SaveButton>
                {dirty && !saving && (
                    <ResetButton onClick={() => setValues(original)}>
                        Reset
                    </ResetButton>
                )}
                <Box sx={{ flex: 1 }} />
                {updatedAt && (
                    <Typography
                        sx={{
                            fontFamily: FONTS.mono,
                            fontSize: '11px',
                            color: VALET.textMuted,
                        }}>
                        Last changed{' '}
                        {new Date(updatedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                        })}
                    </Typography>
                )}
            </Box>

            <Snackbar
                open={!!toast}
                autoHideDuration={5000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                {toast ? (
                    <Alert
                        severity={toast.severity}
                        onClose={() => setToast(null)}
                        sx={{ fontFamily: FONTS.sans }}>
                        {toast.msg}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Box>
    );
}
